import Axios from "axios";

// Display pricing sourced from the imported GSDDPrice table via GET /Drug/{packageId}/pricing.
// Replaces the previous reliance on the live GSDD ListPackagePricesCurrent SOAP data that rode
// along on the drug-details payload (drug.prices). See SD-4281.

// Price type ids (GSDD): 1 = AWP, 13 = Calculated AWP, 2 = WAC.
const AWP_TYPES = [1, 13];
const WAC_TYPE = 2;

export interface DrugPricing {
    packageId: number;
    packagePrice?: number | null;
    unitPrice?: number | null;
    priceTypeId?: number | null;
    packageSize?: number | null;
}

export interface DrugPricingResponse {
    billingUnit: string | null;
    prices: DrugPricing[];
}

/** Fetches AWP/WAC display pricing for a GSDD package from the database source. */
export async function fetchDrugPricing(packageId: number): Promise<DrugPricingResponse> {
    const { data } = await Axios.get<DrugPricingResponse>(`/Drug/${packageId}/pricing`);
    return { billingUnit: data?.billingUnit ?? null, prices: data?.prices ?? [] };
}

function priceString(label: string, amount: number | null | undefined, unit: string): string {
    return amount != null ? `${label}: $${amount}/${unit}` : ``;
}

/** "AWP: $x/EA WAC: $y/EA" using the per-unit price (mirrors the prior drugPrice display). */
export function formatUnitPricing(pricing: DrugPricingResponse | null): string {
    if (!pricing?.prices?.length) return ``;
    const unit = pricing.billingUnit ?? ``;
    const awp = pricing.prices.find(p => AWP_TYPES.includes(p.priceTypeId ?? -1));
    const wac = pricing.prices.find(p => p.priceTypeId === WAC_TYPE);
    return `${priceString("AWP", awp?.unitPrice, unit)} ${priceString("WAC", wac?.unitPrice, unit)}`.trim();
}

/** "AWP: $(unit*size)/EA WAC: ..." using the full-package price (mirrors the prior drugPackagePrice display). */
export function formatPackagePricing(pricing: DrugPricingResponse | null): string {
    if (!pricing?.prices?.length) return ``;
    const unit = pricing.billingUnit ?? ``;
    const awp = pricing.prices.find(p => AWP_TYPES.includes(p.priceTypeId ?? -1));
    const wac = pricing.prices.find(p => p.priceTypeId === WAC_TYPE);
    const pkg = (p?: DrugPricing) => (p?.unitPrice != null && p?.packageSize != null ? p.unitPrice * p.packageSize : null);
    return `${priceString("AWP", pkg(awp), unit)} ${priceString("WAC", pkg(wac), unit)}`.trim();
}
